import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, resolveImageUrl, uploadImage } from "@/lib/api";
import PostCard from "@/components/PostCard";
import FollowListDialog from "@/components/FollowListDialog";
import CollegeAutocomplete from "@/components/CollegeAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, School, MapPin, Users, Pencil, MessageSquare, BadgeCheck, ImagePlus, KeyRound } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

function initials(name = "?") { return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, meta, updateMe, refresh, setFollowing } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMe = !id || id === user?.id;
  const [edit, setEdit] = useState({});
  const [regNum, setRegNum] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followPending, setFollowPending] = useState(false);
  const [followListMode, setFollowListMode] = useState(null); // "followers" | "following" | null
  const avatarRef = useRef(null);

  const load = async () => {
    try {
      const targetId = isMe ? user.id : id;
      const { data } = await api.get(`/users/${targetId}`);
      setProfile(data);
      setEdit({
        full_name: data.full_name, bio: data.bio || "", specialty: data.specialty || "",
        college: data.college, university: data.university, batch_year: data.batch_year,
        degree: data.degree, avatar_url: data.avatar_url || "", location: data.location || "",
        md_college: data.md_college || "", md_batch_year: data.md_batch_year || "",
        ms_college: data.ms_college || "", ms_batch_year: data.ms_batch_year || "",
        phd_college: data.phd_college || "", phd_batch_year: data.phd_batch_year || "",
      });
      setIsFollowing((data.followers || []).includes(user.id));
      const { data: userPosts } = await api.get("/posts", { params: { author_id: targetId } });
      setPosts(userPosts);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  const toggleFollow = async () => {
    if (followPending) return;
    const wasFollowing = isFollowing;
    setFollowPending(true);
    setIsFollowing(!wasFollowing); // optimistic
    try {
      if (wasFollowing) await api.delete(`/users/${profile.id}/follow`);
      else await api.post(`/users/${profile.id}/follow`);
      setFollowing(profile.id, !wasFollowing);
      load();
    } catch (e) {
      setIsFollowing(wasFollowing); // revert
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setFollowPending(false); }
  };

  const startChat = async () => {
    try {
      const { data } = await api.post("/conversations", { peer_id: profile.id });
      navigate(`/app/chat/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onAvatarFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const path = await uploadImage(f);
      setEdit((prev) => ({ ...prev, avatar_url: path }));
      toast.success("Avatar uploaded — save profile to apply");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMe({
        ...edit,
        batch_year: edit.batch_year ? parseInt(edit.batch_year, 10) : undefined,
        specialty: edit.specialty || null,
        avatar_url: edit.avatar_url || null,
        md_college: edit.md_college?.trim() || null,
        md_batch_year: edit.md_batch_year ? parseInt(edit.md_batch_year, 10) : null,
        ms_college: edit.ms_college?.trim() || null,
        ms_batch_year: edit.ms_batch_year ? parseInt(edit.ms_batch_year, 10) : null,
        phd_college: edit.phd_college?.trim() || null,
        phd_batch_year: edit.phd_batch_year ? parseInt(edit.phd_batch_year, 10) : null,
      });
      toast.success("Profile updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    if (!regNum.trim()) return;
    try {
      await api.post("/users/me/verify", { registration_number: regNum.trim() });
      toast.success("Verified! Your gold seal is now live.");
      setVerifyOpen(false);
      await refresh();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match"); return; }
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setPwSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pw.current, new_password: pw.next,
      });
      if (data.token) localStorage.setItem("ayur_token", data.token);
      toast.success("Password updated");
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    } finally { setPwSaving(false); }
  };

  if (!profile) return <div className="text-center py-16 font-serif-display text-2xl text-[color:var(--ayur-emerald)]">Loading…</div>;

  return (
    <div data-testid="user-profile-container" className="max-w-4xl mx-auto space-y-6">
      <div className="paper-card overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-[color:var(--ayur-emerald)] to-[color:var(--ayur-sage)]" />
        <div className="p-6 -mt-14">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <Avatar className="w-24 h-24 border-4 border-[color:var(--ayur-cream)] shadow-lg">
              <AvatarImage src={resolveImageUrl(profile.avatar_url)} />
              <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-3xl font-semibold">
                {initials(profile.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex gap-2 flex-wrap">
              {isMe ? (
                <>
                  {!profile.verified && (
                    <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="get-verified-button" variant="outline"
                          className="border-[color:var(--ayur-gold)] text-[color:var(--ayur-emerald)]">
                          <BadgeCheck className="w-4 h-4 mr-2 text-[color:var(--ayur-gold)]" /> Get verified
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[color:var(--ayur-cream)] max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-serif-display text-2xl">Get your gold seal</DialogTitle>
                          <DialogDescription className="text-[color:var(--ayur-mute)]">
                            Add your BAMS registration number (self-declared) to earn a verified badge next to your name.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submitVerify} className="space-y-3">
                          <Label>Registration number</Label>
                          <Input
                            data-testid="verify-reg-number-input"
                            value={regNum} onChange={(e) => setRegNum(e.target.value)}
                            placeholder="e.g. CCIM/BAMS/2020/12345" className="bg-white"
                          />
                          <Button type="submit" data-testid="verify-submit-button"
                            className="w-full bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white rounded-full py-6">
                            Verify me
                          </Button>
                          <p className="text-xs text-[color:var(--ayur-mute)]">
                            Self-declared at signup — admin may audit periodically. False numbers can be reported by peers.
                          </p>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="edit-profile-button" variant="outline" className="border-[color:var(--ayur-border)]">
                        <Pencil className="w-4 h-4 mr-2" /> Edit profile
                      </Button>
                    </DialogTrigger>                    <DialogContent className="bg-[color:var(--ayur-cream)] max-w-md max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">Edit profile</DialogTitle>
                        <DialogDescription className="text-[color:var(--ayur-mute)]">
                          Update your details, add a bio, and upload a profile photo.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={save} className="space-y-3">
                        <div className="flex items-center gap-4">
                          <Avatar className="w-16 h-16 border border-[color:var(--ayur-border)]">
                            <AvatarImage src={resolveImageUrl(edit.avatar_url)} />
                            <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)]">
                              {initials(edit.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <input ref={avatarRef} type="file" hidden accept="image/*" onChange={onAvatarFile}
                              data-testid="avatar-file-input" />
                            <Button type="button" variant="outline" size="sm"
                              data-testid="avatar-upload-button"
                              onClick={() => avatarRef.current?.click()} disabled={uploading}
                              className="border-[color:var(--ayur-border)]">
                              <ImagePlus className="w-4 h-4 mr-2" />
                              {uploading ? "Uploading…" : "Upload photo"}
                            </Button>
                          </div>
                        </div>
                        <div><Label>Full name</Label>
                          <Input value={edit.full_name} onChange={(e) => setEdit({ ...edit, full_name: e.target.value })} className="bg-white" />
                        </div>
                        <div><Label>Bio</Label>
                          <Textarea rows={3} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} className="bg-white resize-none" />
                        </div>
                        <div><Label>Location</Label>
                          <Input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} className="bg-white" placeholder="Pune, India" />
                        </div>
                        <div><Label>College</Label>
                          <CollegeAutocomplete
                            value={edit.college}
                            onChange={(v) => setEdit({ ...edit, college: v })}
                            testid="edit-college-input"
                          />
                        </div>
                        <div><Label>University</Label>
                          <Input value={edit.university} onChange={(e) => setEdit({ ...edit, university: e.target.value })} className="bg-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Batch year</Label>
                            <Input type="number" value={edit.batch_year} onChange={(e) => setEdit({ ...edit, batch_year: e.target.value })} className="bg-white" />
                          </div>
                          <div><Label>Degree</Label>
                            <Select value={edit.degree} onValueChange={(v) => setEdit({ ...edit, degree: v })}>
                              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>{meta.degrees.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="pt-2">
                          <div className="label-eyebrow text-[color:var(--ayur-terracotta)] mb-2">Postgrad (optional)</div>
                          {[
                            { key: "md", label: "MD (Ayurveda)" },
                            { key: "ms", label: "MS (Ayurveda)" },
                            { key: "phd", label: "PhD (Ayurveda)" },
                          ].map((pg) => (
                            <div key={pg.key} className="grid grid-cols-[1fr_100px] gap-2 mb-2">
                              <CollegeAutocomplete
                                value={edit[`${pg.key}_college`]}
                                onChange={(v) => setEdit({ ...edit, [`${pg.key}_college`]: v })}
                                placeholder={`${pg.label} college`}
                                testid={`edit-postgrad-${pg.key}-college`}
                              />
                              <Input
                                data-testid={`edit-postgrad-${pg.key}-year`}
                                type="number" min="1950" max="2035"
                                value={edit[`${pg.key}_batch_year`]}
                                onChange={(e) => setEdit({ ...edit, [`${pg.key}_batch_year`]: e.target.value })}
                                className="bg-white" placeholder="Year"
                              />
                            </div>
                          ))}
                        </div>
                        <div><Label>Specialty</Label>
                          <Select value={edit.specialty} onValueChange={(v) => setEdit({ ...edit, specialty: v })}>
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Choose" /></SelectTrigger>
                            <SelectContent>{meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Button type="submit" disabled={saving || uploading} className="w-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white rounded-full py-6">
                          {saving ? "Saving…" : "Save"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={pwOpen} onOpenChange={setPwOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="change-password-button" variant="outline" className="border-[color:var(--ayur-border)]">
                        <KeyRound className="w-4 h-4 mr-2" /> Change password
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[color:var(--ayur-cream)] max-w-sm">
                      <DialogHeader>
                        <DialogTitle className="font-serif-display text-2xl">Change password</DialogTitle>
                        <DialogDescription className="text-[color:var(--ayur-mute)]">
                          Set a new password. You'll stay logged in on this device.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={submitPasswordChange} className="space-y-3">
                        <div>
                          <Label>Current password</Label>
                          <Input
                            data-testid="change-password-current-input"
                            type="password" autoComplete="current-password"
                            value={pw.current}
                            onChange={(e) => setPw({ ...pw, current: e.target.value })}
                            className="bg-white" required
                          />
                        </div>
                        <div>
                          <Label>New password</Label>
                          <Input
                            data-testid="change-password-new-input"
                            type="password" autoComplete="new-password" minLength={6}
                            value={pw.next}
                            onChange={(e) => setPw({ ...pw, next: e.target.value })}
                            className="bg-white" required placeholder="At least 6 characters"
                          />
                        </div>
                        <div>
                          <Label>Confirm new password</Label>
                          <Input
                            data-testid="change-password-confirm-input"
                            type="password" autoComplete="new-password" minLength={6}
                            value={pw.confirm}
                            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                            className="bg-white" required
                          />
                        </div>
                        <Button
                          type="submit" disabled={pwSaving}
                          data-testid="change-password-submit-button"
                          className="w-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white rounded-full py-6"
                        >
                          {pwSaving ? "Updating…" : "Update password"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <>
                  <Button data-testid="message-scholar-button" onClick={startChat} variant="outline"
                    className="border-[color:var(--ayur-border)]">
                    <MessageSquare className="w-4 h-4 mr-2" /> Message
                  </Button>
                  <Button data-testid="follow-scholar-button" onClick={toggleFollow} disabled={followPending}
                    className={isFollowing
                      ? "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-badge)]"
                      : "bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"}>
                    {followPending ? "…" : (isFollowing ? "Following" : "Follow")}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="mt-4">
            <h1 className="font-serif-display text-3xl text-[color:var(--ayur-emerald)] inline-flex items-center gap-2">
              {profile.full_name}
              {profile.verified && <VerifiedBadge size={22} />}
            </h1>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="pill" data-testid="user-specialty-badge"><GraduationCap className="w-3.5 h-3.5" />{profile.degree}</span>
              {profile.specialty && <span className="pill">{profile.specialty}</span>}
              <span className="pill" data-testid="user-batch-year-badge">Batch {profile.batch_year}</span>
              {profile.verified && profile.registration_number && (
                <span className="pill" style={{ background: "rgba(212,163,89,0.15)", borderColor: "var(--ayur-gold)", color: "#8a6a2c" }}
                  data-testid="verified-source-pill"
                  title={profile.verified_source === "registry" ? "Verified via CCIM/NCISM format check" : "Self-declared"}>
                  {profile.verified_source === "registry" ? "✓ Registry-verified" : "Self-declared"}: {profile.registration_number}
                </span>
              )}
            </div>

            {profile.bio && (
              <div data-testid="user-bio-section" className="mt-4 text-[color:var(--ayur-ink-2)] leading-relaxed">
                {profile.bio}
              </div>
            )}

            <div className="mt-4 text-sm text-[color:var(--ayur-ink-2)] space-y-1">
              <div className="flex items-center gap-2" data-testid="user-college-badge">
                <School className="w-4 h-4 text-[color:var(--ayur-mute)]" />
                <span>BAMS · {profile.college} · {profile.university} · Batch {profile.batch_year}</span>
              </div>
              {profile.md_college && (
                <div className="flex items-center gap-2" data-testid="user-md-badge">
                  <School className="w-4 h-4 text-[color:var(--ayur-mute)]" />
                  <span>MD (Ayurveda) · {profile.md_college}{profile.md_batch_year ? ` · Batch ${profile.md_batch_year}` : ""}</span>
                </div>
              )}
              {profile.ms_college && (
                <div className="flex items-center gap-2" data-testid="user-ms-badge">
                  <School className="w-4 h-4 text-[color:var(--ayur-mute)]" />
                  <span>MS (Ayurveda) · {profile.ms_college}{profile.ms_batch_year ? ` · Batch ${profile.ms_batch_year}` : ""}</span>
                </div>
              )}
              {profile.phd_college && (
                <div className="flex items-center gap-2" data-testid="user-phd-badge">
                  <School className="w-4 h-4 text-[color:var(--ayur-mute)]" />
                  <span>PhD (Ayurveda) · {profile.phd_college}{profile.phd_batch_year ? ` · Batch ${profile.phd_batch_year}` : ""}</span>
                </div>
              )}
              {profile.location && (
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[color:var(--ayur-mute)]" />{profile.location}</div>
              )}
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[color:var(--ayur-mute)]" />
                <button
                  type="button"
                  data-testid="open-followers-list"
                  onClick={() => setFollowListMode("followers")}
                  className="hover:underline text-[color:var(--ayur-emerald)] font-medium"
                >
                  {profile.followers?.length || 0} followers
                </button>
                <span className="text-[color:var(--ayur-mute)]">·</span>
                <button
                  type="button"
                  data-testid="open-following-list"
                  onClick={() => setFollowListMode("following")}
                  className="hover:underline text-[color:var(--ayur-emerald)] font-medium"
                >
                  {profile.following?.length || 0} following
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-serif-display text-2xl text-[color:var(--ayur-emerald)] mb-4">Posts</h2>
        {posts.length === 0 ? (
          <div className="paper-card p-12 text-center text-[color:var(--ayur-mute)]">No posts yet.</div>
        ) : (
          <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)}</div>
        )}
      </div>

      <FollowListDialog
        open={followListMode !== null}
        onOpenChange={(v) => { if (!v) setFollowListMode(null); }}
        userId={profile.id}
        mode={followListMode || "followers"}
        ownerName={isMe ? "you" : profile.full_name}
        onChanged={async () => { await refresh(); load(); }}
      />
    </div>
  );
}
